import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Users, Phone, Target, Calendar, ArrowRight, Trash2, Lock, Globe, Swords } from 'lucide-react';
import { PRESTATAIRES } from '@/constants/prestataires';
import { toast } from 'sonner';

const COLORS = ['#F97316', '#0055FF', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B'];

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nom: '', description: '', couleur: '#F97316', visibilite: 'public', concurrent: 'Aucun', concurrent_autre: '' });
  const navigate = useNavigate();

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get('/campaigns');
      setCampaigns(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadCampaigns(); }, []);

  const createCampaign = async (e) => {
    e.preventDefault();
    const concurrentFinal = form.concurrent === 'Autre' ? (form.concurrent_autre || '').trim() : (form.concurrent === 'Aucun' ? '' : form.concurrent);
    try {
      const payload = { nom: form.nom, description: form.description, couleur: form.couleur, visibilite: form.visibilite, concurrent: concurrentFinal };
      const { data } = await api.post('/campaigns', payload);
      toast.success(`Campagne "${data.nom}" créée (${data.visibilite === 'privee' ? 'Privée' : 'Publique'})`);
      setShowCreate(false);
      setForm({ nom: '', description: '', couleur: '#F97316', visibilite: 'public', concurrent: 'Aucun', concurrent_autre: '' });
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const toggleVisibility = async (e, c) => {
    e.stopPropagation();
    const newVis = c.visibilite === 'privee' ? 'public' : 'privee';
    try {
      await api.put(`/campaigns/${c.id}`, { visibilite: newVis });
      toast.success(`Campagne passée en ${newVis === 'privee' ? 'Privée' : 'Publique'}`);
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteCampaign = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette campagne ?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Campagne supprimée');
      loadCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="animate-pulse-subtle text-center py-20 text-[#71717A]">Chargement...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Campagnes</h1>
          <p className="text-sm text-[#71717A] mt-1">Organisez vos actions de prospection par campagne</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-btn" className="h-9 gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white">
              <Plus className="w-4 h-4" /> Nouvelle campagne
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-campaign-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Créer une campagne</DialogTitle>
            </DialogHeader>
            <form onSubmit={createCampaign} className="space-y-4 pt-2">
              <div>
                <Label className="text-sm mb-1.5 block">Nom de la campagne</Label>
                <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="ex: Campagne Système U, Campagne Leclerc..." required
                  className="h-10 bg-white" data-testid="campaign-name-input" />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Description (optionnel)</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Objectifs, périmètre, notes..."
                  className="bg-white" data-testid="campaign-description-input" rows={3} />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Couleur</Label>
                <div className="flex gap-2">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setForm(f => ({ ...f, couleur: c }))}
                      className={`w-8 h-8 rounded-full transition-all ${form.couleur === c ? 'ring-2 ring-offset-2 ring-[#09090B] scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Prestataire en place (concurrent ciblé)</Label>
                <Select value={form.concurrent} onValueChange={v => setForm(f => ({ ...f, concurrent: v }))}>
                  <SelectTrigger className="h-10 bg-white" data-testid="campaign-concurrent-select">
                    <SelectValue placeholder="Sélectionner un prestataire" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESTATAIRES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.concurrent === 'Autre' && (
                  <Input
                    value={form.concurrent_autre}
                    onChange={e => setForm(f => ({ ...f, concurrent_autre: e.target.value }))}
                    placeholder="Préciser le prestataire..."
                    className="h-10 bg-white mt-2"
                    data-testid="campaign-concurrent-autre-input"
                  />
                )}
                <p className="text-xs text-[#71717A] mt-1">Ce prestataire sera associé à tous les prospects de la campagne</p>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Visibilité</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, visibilite: 'public' }))}
                    className={`p-3 rounded-lg border text-left transition-all ${form.visibilite === 'public' ? 'border-[#10B981] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:border-[#D1D5DB]'}`}
                    data-testid="visibility-public-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4 text-[#10B981]" />
                      <span className="text-sm font-semibold">Publique</span>
                    </div>
                    <p className="text-xs text-[#71717A]">Visible par toute l'équipe</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, visibilite: 'privee' }))}
                    className={`p-3 rounded-lg border text-left transition-all ${form.visibilite === 'privee' ? 'border-[#F97316] bg-[#FFF7ED]' : 'border-[#E5E7EB] bg-white hover:border-[#D1D5DB]'}`}
                    data-testid="visibility-private-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="w-4 h-4 text-[#F97316]" />
                      <span className="text-sm font-semibold">Privée</span>
                    </div>
                    <p className="text-xs text-[#71717A]">Visible par vous uniquement</p>
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="submit-create-campaign"
                className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">Créer la campagne</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card className="border border-[#E5E7EB] shadow-sm">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#F97316]/10 flex items-center justify-center mx-auto mb-4">
              <Target className="w-7 h-7 text-[#F97316]" />
            </div>
            <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Aucune campagne</h3>
            <p className="text-sm text-[#71717A] mb-4">Créez votre première campagne pour organiser vos prospects</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#F97316] hover:bg-[#EA580C] text-white gap-2">
              <Plus className="w-4 h-4" /> Créer une campagne
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {campaigns.map(c => (
            <Card key={c.id} className="kpi-card border border-[#E5E7EB] shadow-sm cursor-pointer group"
              onClick={() => navigate(`/campaigns/${c.id}`)} data-testid={`campaign-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.couleur }} />
                    <h3 className="font-semibold text-base truncate" style={{ fontFamily: 'var(--font-heading)' }}>{c.nom}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={e => toggleVisibility(e, c)}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${c.visibilite === 'privee' ? 'bg-[#FFF7ED] text-[#F97316] hover:bg-[#FED7AA]' : 'bg-[#ECFDF5] text-[#059669] hover:bg-[#A7F3D0]'}`}
                      title="Cliquer pour changer la visibilité"
                      data-testid={`campaign-visibility-${c.id}`}
                    >
                      {c.visibilite === 'privee' ? <><Lock className="w-2.5 h-2.5" /> Privée</> : <><Globe className="w-2.5 h-2.5" /> Publique</>}
                    </button>
                    <Badge variant="secondary" className={`text-xs ${c.statut === 'active' ? 'bg-[#ECFDF5] text-[#059669]' : c.statut === 'en_pause' ? 'bg-[#FFFBEB] text-[#D97706]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                      {c.statut === 'active' ? 'Active' : c.statut === 'en_pause' ? 'En pause' : 'Terminée'}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-[#EF4444]"
                      onClick={e => deleteCampaign(e, c.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {c.description && <p className="text-xs text-[#71717A] mb-3 line-clamp-2">{c.description}</p>}
                {c.concurrent && (
                  <div className="mb-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-[#FEF2F2] text-[#B91C1C] font-medium" data-testid={`campaign-concurrent-badge-${c.id}`}>
                      <Swords className="w-3 h-3" /> Cible : {c.concurrent}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-[#71717A]" />
                    <span className="text-sm font-medium">{c.nb_prospects}</span>
                    <span className="text-xs text-[#71717A]">prospects</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#10B981]" />
                    <span className="text-sm font-medium">{c.nb_enrichis}</span>
                    <span className="text-xs text-[#71717A]">enrichis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-3.5 h-3.5 text-[#0055FF]" />
                    <span className="text-sm font-medium">{c.nb_rdv}</span>
                    <span className="text-xs text-[#71717A]">RDV</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-[#F59E0B]" />
                    <span className="text-sm font-medium">{c.nb_relances}</span>
                    <span className="text-xs text-[#71717A]">relances</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
                  <span className="text-xs text-[#71717A]">
                    {new Date(c.cree_le).toLocaleDateString('fr-FR')}
                    {c.cree_par_nom && <span className="ml-1">• par {c.cree_par_nom}</span>}
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#71717A] group-hover:text-[#F97316] transition-colors" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
